import React, { useEffect, useRef, useState } from 'react';
import { loadMercadoPago } from '@mercadopago/sdk-js';
import {
    FiMapPin, FiPhone, FiShield, FiCheck, FiLock,
} from 'react-icons/fi';
import { MP_PUBLIC_KEY } from '../../utils';

const UFS = [
    'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB',
    'PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',
];

const BRICK_CONTAINER_ID = 'cardPaymentBrick_container';

// Form de cartão com CHECKOUT BRICKS (Card Payment Brick) — o widget do Mercado
// Pago renderiza número/validade/CVC/nome/CPF/parcelas/banco emissor e tokeniza
// no navegador (PCI). Nada do cartão passa pelo nosso JS/back: só o TOKEN trafega.
//
// Mantemos NOSSO formulário de endereço de cobrança (reduz recusa) + telefone
// ao lado do Brick. O device fingerprint vem do security.js global
// (window.MP_DEVICE_SESSION_ID), lido pelos callers — não muda aqui.
//
// Devolve via onSubmit({ token, paymentMethodId, issuerId, installments,
// paymentTypeId, billingAddress, phone, cardholderCpf }) — mesmo contrato de antes.
export default function CartaoForm({
    valor,
    paymentTypeId = 'credit_card',
    loading = false,
    onSubmit,
    maxInstallments = 12,
    userPhone,    // se preenchido (só dígitos), esconde campo de telefone
    payerEmail,   // e-mail do pagador; se vier, o Brick não pede e-mail de novo
}) {
    // Endereço de cobrança
    const [cep, setCep] = useState('');
    const [rua, setRua] = useState('');
    const [numero, setNumero] = useState('');
    const [complemento, setComplemento] = useState('');
    const [bairro, setBairro] = useState('');
    const [cidade, setCidade] = useState('');
    const [uf, setUf] = useState('');
    const [cepLoading, setCepLoading] = useState(false);

    // Telefone (só pede se userPhone vazio)
    const userPhoneDigits = String(userPhone || '').replace(/\D/g, '');
    const precisaTelefone = userPhoneDigits.length < 10;
    const [phone, setPhone] = useState('');

    const [errors, setErrors] = useState({});
    const [avisoVisto, setAvisoVisto] = useState(false);

    const [sdkReady, setSdkReady] = useState(false);
    const [brickError, setBrickError] = useState(null);
    const [initError, setInitError] = useState(null);

    const isDebit = paymentTypeId === 'debit_card';

    // Ref "viva" com tudo que o callback do Brick (criado uma vez) precisa ler no
    // momento do submit — evita closure obsoleta sobre o state do React.
    const liveRef = useRef({});
    liveRef.current = {
        onSubmit, paymentTypeId, precisaTelefone, userPhoneDigits,
        cep, rua, numero, complemento, bairro, cidade, uf, phone,
    };

    // === Inicializa o SDK e monta o Card Payment Brick ===
    useEffect(() => {
        let vivo = true;
        let controller = null;

        (async () => {
            if (!MP_PUBLIC_KEY) {
                console.log(MP_PUBLIC_KEY)
                setInitError('Pagamento por cartão indisponível (chave do MP não configurada).');
                return;
            }
            try {
                await loadMercadoPago();
                if (!vivo || !window.MercadoPago) return;

                const mp = new window.MercadoPago(MP_PUBLIC_KEY, { locale: 'pt-BR' });
                const bricks = mp.bricks();

                controller = await bricks.create('cardPayment', BRICK_CONTAINER_ID, {
                    initialization: {
                        amount: Number(valor) || 0,
                        ...(payerEmail ? { payer: { email: payerEmail } } : {}),
                    },
                    customization: {
                        visual: {
                            hidePaymentButton: false,
                            style: {
                                theme: 'default',
                                // Casa o Brick com o verde da UEN (botão Pagar + acentos).
                                customVariables: {
                                    baseColor: '#059669',
                                    baseColorFirstVariant: '#047857',
                                    baseColorSecondVariant: '#10B981',
                                    buttonTextColor: '#FFFFFF',
                                    borderRadiusMedium: '12px',
                                    borderRadiusLarge: '14px',
                                    fontSizeMedium: '15px',
                                },
                            },
                        },
                        paymentMethods: {
                            maxInstallments: isDebit ? 1 : maxInstallments,
                            minInstallments: 1,
                        },
                    },
                    callbacks: {
                        onReady: () => { if (vivo) setSdkReady(true); },
                        onError: (error) => {
                            console.error('[CartaoForm/Brick] erro:', error);
                            if (vivo) setBrickError(error?.message || 'Confira os dados do cartão.');
                        },
                        // Chamado quando o usuário clica em "Pagar" no Brick e o
                        // cartão é válido. No Brick 'cardPayment', a assinatura é
                        // (formData, additionalData): formData traz token, bandeira,
                        // parcelas, identificação (CPF); o paymentTypeId vem em
                        // additionalData. (issuer_id pode vir no formData.)
                        onSubmit: (formData, additionalData) => {
                            const L = liveRef.current;
                            // Valida NOSSO endereço/telefone antes de cobrar.
                            if (!validarEndereco(L, setErrors)) {
                                setBrickError('Complete o endereço de cobrança acima antes de pagar.');
                                return Promise.reject(new Error('endereco_incompleto'));
                            }
                            setBrickError(null);
                            const finalPhone = L.precisaTelefone
                                ? String(L.phone).replace(/\D/g, '')
                                : L.userPhoneDigits;

                            // RETORNAMOS a promise do caller (não resolvemos na hora).
                            // O Brick fica "processando" e mantém o botão travado até o
                            // backend responder. Se o caller REJEITAR (recusa/erro), o
                            // Brick re-habilita e RE-TOKENIZA no próximo "Pagar" — o
                            // token do cartão é de USO ÚNICO, então sem isso um retry
                            // reenviaria o token já consumido e o MP responde
                            // invalid_card_token. (Resolver na hora também re-habilitava
                            // o botão cedo demais → risco de cobrança dupla.)
                            return Promise.resolve(
                                L.onSubmit({
                                    token: formData.token,
                                    paymentMethodId: formData.payment_method_id,
                                    issuerId: formData.issuer_id,
                                    installments: Number(formData.installments) || 1,
                                    paymentTypeId: additionalData?.paymentTypeId || formData.payment_type_id || L.paymentTypeId,
                                    billingAddress: {
                                        cep: String(L.cep).replace(/\D/g, ''),
                                        rua: L.rua.trim(),
                                        numero: L.numero.trim(),
                                        complemento: L.complemento.trim() || null,
                                        bairro: L.bairro.trim(),
                                        cidade: L.cidade.trim(),
                                        uf: L.uf.trim().toUpperCase(),
                                    },
                                    phone: finalPhone,
                                    cardholderCpf: formData?.payer?.identification?.number || '',
                                })
                            );
                        },
                    },
                });
                // StrictMode (dev) pode rodar o cleanup antes do create resolver.
                // Se já desmontou, joga fora esse Brick recém-criado.
                if (!vivo) { try { controller?.unmount?.(); } catch { /* noop */ } }
            } catch (e) {
                console.error('[CartaoForm] Falha ao iniciar o Brick:', e);
                if (vivo) setInitError('Não foi possível carregar o formulário de cartão. Recarregue a página.');
            }
        })();

        return () => {
            vivo = false;
            try { controller?.unmount?.(); } catch { /* noop */ }
        };
        // Recria se o valor, o tipo (crédito/débito) ou o e-mail do pagador mudarem.
    }, [valor, paymentTypeId, payerEmail, isDebit, maxInstallments]);

    // === Máscaras (campos não sensíveis) ===
    function maskCep(v) {
        const d = String(v || '').replace(/\D/g, '').slice(0, 8);
        return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
    }
    function maskPhone(v) {
        const d = String(v || '').replace(/\D/g, '').slice(0, 11);
        if (d.length <= 2) return d;
        if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
        if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
        return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    }

    // === Lookup CEP ===
    async function buscarCep(cepMasked) {
        const digits = String(cepMasked || '').replace(/\D/g, '');
        if (digits.length !== 8) return;
        setCepLoading(true);
        try {
            const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
            const data = await res.json();
            if (!data?.erro) {
                setRua(data.logradouro || '');
                setBairro(data.bairro || '');
                setCidade(data.localidade || '');
                setUf((data.uf || '').toUpperCase());
            }
        } catch {
            // ignora — usuário pode preencher manual
        } finally {
            setCepLoading(false);
        }
    }
    function handleCepChange(e) {
        const masked = maskCep(e.target.value);
        setCep(masked);
        if (masked.replace(/\D/g, '').length === 8) buscarCep(masked);
    }

    const ocupado = loading;

    return (
        <>
            {!avisoVisto && <AvisoCartaoModal onContinuar={() => setAvisoVisto(true)} />}
            <div style={styles.form}>

                {/* === Endereço de cobrança (NOSSO form) === */}
                <SectionTitle subtitle="Mesma cobrança da fatura do cartão. Reduz a chance de recusa.">
                    Endereço de cobrança
                </SectionTitle>

                <Field label="CEP" icon={<FiMapPin size={18} />} error={errors.cep}>
                    <input
                        type="text" inputMode="numeric" placeholder="00000-000" maxLength={9}
                        value={cep} onChange={handleCepChange}
                        disabled={ocupado} style={styles.input(!!errors.cep)}
                    />
                    {cepLoading && <small style={styles.hint}>Buscando endereço…</small>}
                </Field>

                <Field label="Rua / Logradouro" error={errors.rua}>
                    <input
                        type="text" placeholder="Rua, avenida…"
                        value={rua} onChange={e => setRua(e.target.value)}
                        disabled={ocupado} style={styles.input(!!errors.rua)}
                    />
                </Field>

                <div style={styles.row}>
                    <Field label="Número" error={errors.numero} flex={1}>
                        <input
                            type="text" placeholder="123"
                            value={numero} onChange={e => setNumero(e.target.value)}
                            disabled={ocupado} style={styles.input(!!errors.numero)}
                        />
                    </Field>
                    <Field label="Complemento (opcional)" flex={2}>
                        <input
                            type="text" placeholder="Apto, bloco…"
                            value={complemento} onChange={e => setComplemento(e.target.value)}
                            disabled={ocupado} style={styles.input(false)}
                        />
                    </Field>
                </div>

                <Field label="Bairro">
                    <input
                        type="text" placeholder="Bairro"
                        value={bairro} onChange={e => setBairro(e.target.value)}
                        disabled={ocupado} style={styles.input(false)}
                    />
                </Field>

                <div style={styles.row}>
                    <Field label="Cidade" error={errors.cidade} flex={2}>
                        <input
                            type="text" placeholder="Cidade"
                            value={cidade} onChange={e => setCidade(e.target.value)}
                            disabled={ocupado} style={styles.input(!!errors.cidade)}
                        />
                    </Field>
                    <Field label="UF" error={errors.uf} flex={1}>
                        <select
                            value={uf} onChange={e => setUf(e.target.value)}
                            disabled={ocupado} style={styles.input(!!errors.uf)}
                        >
                            <option value="">UF</option>
                            {UFS.map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                    </Field>
                </div>

                {/* === Telefone (só se user ainda não tem) === */}
                {precisaTelefone && (
                    <>
                        <SectionTitle subtitle="Usado pelo Mercado Pago pra validar o pagamento.">
                            Telefone
                        </SectionTitle>
                        <Field label="Telefone com DDD" icon={<FiPhone size={18} />} error={errors.phone}>
                            <input
                                type="tel" inputMode="tel" placeholder="(00) 00000-0000"
                                value={phone} onChange={e => setPhone(maskPhone(e.target.value))}
                                disabled={ocupado} autoComplete="tel" maxLength={15}
                                style={styles.input(!!errors.phone)}
                            />
                        </Field>
                    </>
                )}

                {/* === Dados do cartão (Brick do Mercado Pago) === */}
                <SectionTitle>Dados do cartão</SectionTitle>

                {initError && <div style={styles.errorBox}>{initError}</div>}
                {brickError && <div style={styles.errorBox}>{brickError}</div>}
                {!sdkReady && !initError && (
                    <div style={styles.brickLoading}>Carregando formulário seguro do Mercado Pago…</div>
                )}

                {/* Container onde o Brick é montado (inclui o botão "Pagar"). */}
                <div id={BRICK_CONTAINER_ID} style={{ minHeight: sdkReady ? undefined : 1 }} />
            </div>
        </>
    );
}

// Valida o endereço/telefone (lendo da ref viva) e popula os erros. Retorna bool.
function validarEndereco(L, setErrors) {
    const next = {};
    if (String(L.cep).replace(/\D/g, '').length !== 8) next.cep = 'CEP inválido.';
    if (!L.rua.trim()) next.rua = 'Informe a rua.';
    if (!L.numero.trim()) next.numero = 'Informe o número.';
    if (!L.cidade.trim()) next.cidade = 'Informe a cidade.';
    if (!L.uf.trim() || L.uf.length !== 2) next.uf = 'Informe a UF.';
    if (L.precisaTelefone && String(L.phone).replace(/\D/g, '').length < 10) {
        next.phone = 'Informe o telefone com DDD.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
}

// Modal informativo (segurança) exibido antes do formulário de cartão.
function AvisoCartaoModal({ onContinuar }) {
    return (
        <div style={avisoStyles.overlay}>
            <div style={avisoStyles.card}>
                <div style={avisoStyles.iconBox}><FiShield size={26} /></div>
                <h3 style={avisoStyles.titulo}>Uma dica de segurança</h3>
                <p style={avisoStyles.texto}>
                    Recomendamos pagar com um <b>cartão virtual</b> ou <b>temporário (24h)</b> —
                    não só aqui, mas em <b>qualquer site</b>. É uma camada extra de proteção
                    pros seus dados.
                </p>
                <div style={avisoStyles.selo}>
                    <FiLock size={15} /> 
                    <span>
                        Os dados do seu cartão são digitados em campos seguros do <b>Mercado
                        Pago</b> e vão criptografados direto pra ele — a UEN <b>não vê nem
                        armazena</b> nenhuma informação do cartão.
                    </span>
                </div>
                <button type="button" onClick={onContinuar} style={avisoStyles.botao}>
                    <FiCheck size={16} /> Entendi, continuar
                </button>
            </div>
        </div>
    );
}

const avisoStyles = {
    overlay: {
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(2px)',
        display: 'grid', placeItems: 'center', padding: 20,
    },
    card: {
        width: '100%', maxWidth: 420, background: '#FFFFFF', borderRadius: 16,
        padding: '1.6rem 1.5rem', textAlign: 'center',
        fontFamily: "'Inter','Roboto',system-ui,sans-serif",
        boxShadow: '0 24px 48px -12px rgba(15,23,42,0.3)',
    },
    iconBox: {
        width: 52, height: 52, borderRadius: 14, margin: '0 auto 0.9rem',
        background: 'linear-gradient(135deg, #059669, #10B981)', color: '#FFFFFF',
        display: 'grid', placeItems: 'center',
    },
    titulo: { fontSize: '1.2rem', fontWeight: 800, color: '#0F172A', margin: '0 0 0.5rem' },
    texto: { fontSize: '0.92rem', color: '#475569', lineHeight: 1.55, margin: '0 0 1rem' },
    selo: {
        display: 'flex', gap: 10, alignItems: 'flex-start', textAlign: 'left',
        background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 12,
        padding: '0.8rem 0.9rem', color: '#065F46', fontSize: '0.82rem', lineHeight: 1.5,
        marginBottom: '1.2rem',
    },
    botao: {
        width: '100%', padding: '0.85rem', borderRadius: 10, border: 'none', cursor: 'pointer',
        background: 'linear-gradient(135deg, #059669, #10B981)', color: '#FFFFFF',
        fontWeight: 700, fontSize: '0.92rem', fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    },
};

function SectionTitle({ children, subtitle }) {
    return (
        <div style={styles.sectionWrap}>
            <h4 style={styles.sectionTitle}>{children}</h4>
            {subtitle && <p style={styles.sectionSubtitle}>{subtitle}</p>}
        </div>
    );
}

function Field({ label, icon, error, flex, children }) {
    return (
        <div style={{ ...styles.fieldWrap, ...(flex ? { flex } : {}) }}>
            <label style={styles.label}>{label}</label>
            <div className="cartao-fieldwrap" style={styles.inputWrap}>
                {icon && <span style={styles.inputIcon}>{icon}</span>}
                <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>{children}</div>
            </div>
            {error && <div style={styles.errorText}>{error}</div>}
        </div>
    );
}

const styles = {
    form: { display: 'flex', flexDirection: 'column', gap: '0.6rem' },
    row: { display: 'flex', gap: '0.6rem' },
    sectionWrap: { margin: '0.6rem 0 0' },
    sectionTitle: {
        margin: 0, fontSize: '0.88rem', fontWeight: 700, color: '#0F172A',
        borderTop: '1px solid #E2E8F0', paddingTop: '0.6rem',
    },
    sectionSubtitle: { margin: '2px 0 0', fontSize: '0.74rem', color: '#64748B' },
    fieldWrap: { display: 'flex', flexDirection: 'column' },
    label: { fontSize: '0.76rem', fontWeight: 600, color: '#475569', marginBottom: '0.25rem' },
    inputWrap: {
        display: 'flex', alignItems: 'center',
        background: '#F8FAFC', border: '1.5px solid #E2E8F0', borderRadius: 12,
        padding: '0 0.8rem', minHeight: 48, boxSizing: 'border-box',
    },
    inputIcon: { color: '#94A3B8', display: 'flex', alignItems: 'center', paddingRight: '0.6rem' },
    input: (hasError) => ({
        width: '100%', border: 'none', outline: 'none', background: 'transparent',
        height: 46, fontSize: '0.95rem',
        color: hasError ? '#9F1239' : '#0F172A', fontFamily: 'inherit',
    }),
    brickLoading: {
        fontSize: '0.82rem', color: '#64748B', background: '#F8FAFC',
        border: '1px dashed #E2E8F0', borderRadius: 10, padding: '0.8rem',
        textAlign: 'center',
    },
    errorText: { color: '#9F1239', fontSize: '0.74rem', marginTop: '0.2rem' },
    errorBox: {
        color: '#9F1239', fontSize: '0.8rem', background: '#FFF1F2',
        border: '1px solid #FECDD3', borderRadius: 8, padding: '0.5rem 0.75rem',
    },
    hint: { color: '#11A697', fontSize: '0.74rem', marginTop: '0.2rem' },
};
